/**
 * Os três estados da leitura do resumo (ISSUE-210).
 *
 * **POR QUE UM HOOK E NÃO O STORE GLOBAL.** O resumo é estado de UMA tela. No
 * Zustand, a segunda tela o leria sem saber quando foi buscado — que é o
 * anti-padrão do valor velho, o mesmo que esta entrega existe para matar.
 *
 * **O VOCABULÁRIO É EMPRESTADO, A BIBLIOTECA NÃO.** O TanStack Query separa
 * `status` (tenho dado?) de `fetchStatus` (está buscando?), e é essa separação
 * que a spec pede. Adotar a biblioteca resolveria mais do que uma tela com um
 * endpoint justifica; a dívida está nomeada, com condição de quitação: quando a
 * segunda tela precisar do mesmo dado.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import type { Resumo } from '@/schemas/dashboard'
import { dashboardService } from '@/services/dashboard'

export type Situacao = 'buscando' | 'falhou' | 'pronto'

interface Estado {
  situacao: Situacao
  dado: Resumo | null
  rebuscar: () => void
}

export function useResumoDoPainel(organizationId: string | undefined): Estado {
  const [situacao, setSituacao] = useState<Situacao>('buscando')
  const [dado, setDado] = useState<Resumo | null>(null)
  const [tentativa, setTentativa] = useState(0)

  // Qual organização a última busca disparada pertence. Uma resposta que chegue
  // depois de a pessoa ter trocado é DESCARTADA por esta comparação — o
  // `AbortController` resolve o caso comum, e isto resolve a corrida que sobra.
  const emVoo = useRef<string | undefined>(undefined)

  const rebuscar = useCallback(() => setTentativa((n) => n + 1), [])

  useEffect(() => {
    if (!organizationId) return

    const controle = new AbortController()
    emVoo.current = organizationId

    // **O DADO ANTERIOR SAI DA TELA AQUI.** Mantê-lo enquanto busca mostraria
    // número de outra organização como se fosse desta.
    setSituacao('buscando')
    setDado(null)

    dashboardService
      .resumo(organizationId, controle.signal)
      .then((resumo) => {
        if (emVoo.current !== organizationId) return
        setDado(resumo)
        setSituacao('pronto')
      })
      .catch((erro) => {
        if (controle.signal.aborted) return
        if (emVoo.current !== organizationId) return
        // **NADA DE VALOR ANTERIOR** — e quem garante isso é o `setDado(null)` do
        // começo do efeito, não esta linha: quando o `catch` roda, `dado` já é
        // nulo há um render. Esta é cinto sobre suspensório, para o dia em que
        // alguém tirar a limpeza de cima achando que ela só pisca a tela. O
        // comentário anterior dava a garantia por esta linha, e nenhum teste
        // consegue distinguir as duas. Achado na revisão de código.
        setDado(null)
        setSituacao('falhou')
        console.error('não consegui ler o resumo do painel', erro)
      })

    return () => controle.abort()
  }, [organizationId, tentativa])

  return { situacao, dado, rebuscar }
}
